import React, { useState, useEffect, useRef } from 'react';
import { sendChatMessage } from '../api';

/* Экранирование — любые кавычки/переносы в данных не ломают разметку */
const escapeHtml = (value = '') =>
    String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');

const formatPrice = (price) => {
    const num = Number(price) || 0;
    return `${num.toLocaleString('ru-RU')} ₽`;
};

/* Хук для ширины экрана с реакцией на resize */
function useIsMobile(breakpoint = 640) {
    const [isMobile, setIsMobile] = useState(
        typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
    );
    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < breakpoint);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [breakpoint]);
    return isMobile;
}

/* Карточка товара: все стили inline, чтобы не зависеть от index.css */
const buildProductCard = (p) => {
    const id = escapeHtml(String(p.id));
    const photo = p.photo || 'https://placehold.co/96x96/F5F0EB/6B7A6A?text=PS';
    return `<div style="display:flex;align-items:flex-start;gap:10px;margin-top:8px;padding:10px;background:#ffffff;border:1px solid #E8E3DD;border-radius:12px;"> <img src="${escapeHtml(photo)}" alt="${escapeHtml(p.name)}" style="width:56px;height:56px;object-fit:cover;border-radius:10px;flex-shrink:0;background-color:#F5F0EB;" /> <div style="flex:1;min-width:0;"> <span data-open-product="${id}" style="display:block;font-size:0.85rem;font-weight:600;color:#1C1917;line-height:1.3;cursor:pointer;text-decoration:underline;margin-bottom:8px;word-break:break-word;"> ${escapeHtml(p.name)} </span> <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;flex-wrap:wrap;"> <span style="font-size:0.9rem;font-weight:700;color:#6B7A6A;white-space:nowrap;">${formatPrice(p.price)}</span> <button type="button" data-add-cart="${id}" style="background:#6B7A6A;color:#ffffff;border:none;border-radius:100px;padding:8px 16px;font-size:0.78rem;font-weight:600;cursor:pointer;box-shadow:0 4px 12px rgba(107,122,106,0.35);white-space:nowrap;"> + В корзину </button> </div> </div> </div>`;
};

const ChatBot = ({ userId, addToCart, onProductClick }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        { from: 'bot', text: 'Здравствуйте! Я помогу вам подобрать идеальное средство. Напишите, что вас интересует.' },
    ]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [showGreeting, setShowGreeting] = useState(false);

    const isMobile = useIsMobile(640);

    // Товары, предложенные ботом: в HTML идёт только id, данные храним здесь
    const productsById = useRef(new Map());
    const bottomRef = useRef(null);

    // Показываем приветствие через 10 секунд — но только если пользователь его не закрыл в этой сессии
    useEffect(() => {
        try {
            if (sessionStorage.getItem('ps_chatbot_greeting_dismissed') === '1') return;
        } catch (_) { }
        const timer = setTimeout(() => {
            setShowGreeting(true);
        }, 10000);
        return () => clearTimeout(timer);
    }, []);

    // Открываем чат по внешнему событию (кнопки «Спросить AI-консультанта»)
    useEffect(() => {
        const handleOpenEvent = () => {
            setIsOpen(true);
            setShowGreeting(false); // прячем облачко при открытии чата
        };
        window.addEventListener('open-chatbot', handleOpenEvent);
        return () => window.removeEventListener('open-chatbot', handleOpenEvent);
    }, []);

    useEffect(() => {
        window.__addToCart = addToCart;
        window.__openProductModal = onProductClick;
    }, [addToCart, onProductClick]);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, loading, isOpen]);

    const handleSend = async () => {
        if (!input.trim() || loading) return;
        const userMsg = input.trim();
        setMessages((prev) => [...prev, { from: 'user', text: userMsg }]);
        setInput('');
        setLoading(true);
        try {
            const data = await sendChatMessage(userMsg, userId);
            const botReply = data.response || 'Ответ не получен.';
            setMessages((prev) => [
                ...prev,
                { from: 'bot', html: escapeHtml(botReply).replace(/\n/g, '<br>') },
            ]);
            if (data.products && data.products.length) {
                data.products.forEach((p) => productsById.current.set(String(p.id), p));
                const cardsHtml = data.products.map(buildProductCard).join('');
                setMessages((prev) => [
                    ...prev,
                    { from: 'bot', html: `🛍️ Подходящие товары:${cardsHtml}`, wide: true },
                ]);
            }
        } catch (error) {
            console.error('Ошибка чата:', error);
            setMessages((prev) => [...prev, { from: 'bot', text: '⚠️ Ошибка. Попробуйте позже.' }]);
        }
        setLoading(false);
    };

    // Делегирование кликов: в HTML нет inline-JS с данными — сломаться нечему
    const handleMessagesClick = (e) => {
        const addBtn = e.target.closest('[data-add-cart]');
        if (addBtn) {
            const product = productsById.current.get(addBtn.getAttribute('data-add-cart'));
            if (product) addToCart(product);
            return;
        }
        const openEl = e.target.closest('[data-open-product]');
        if (openEl) {
            const product = productsById.current.get(openEl.getAttribute('data-open-product'));
            if (product) onProductClick(product);
        }
    };

    const handleOpen = () => {
        setIsOpen(true);
        setShowGreeting(false); // Скрываем приветствие при открытии чата
    };

    return (
        <>
            {/* Приветственное облачко — появляется через 10 секунд */}
            {showGreeting && !isOpen && (
                <div
                    onClick={handleOpen}
                    style={{
                        position: 'fixed',
                        bottom: isMobile ? '84px' : '100px',
                        right: isMobile ? '16px' : '100px',
                        left: isMobile ? '16px' : 'auto',
                        backgroundColor: 'white',
                        borderRadius: '20px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                        border: '1px solid #E8E3DD',
                        padding: '14px 18px',
                        paddingRight: '38px',
                        maxWidth: isMobile ? 'calc(100vw - 32px)' : '280px',
                        zIndex: 998,
                        cursor: 'pointer',
                        animation: 'fadeSlideIn 0.4s ease-out',
                        boxSizing: 'border-box',
                    }}
                >
                    <span
                        style={{
                            fontSize: '0.9rem',
                            lineHeight: '1.5',
                            color: '#1C1917',
                            display: 'block',
                            wordBreak: 'break-word',
                        }}
                    >
                        Здравствуйте! Я помогу вам подобрать идеальное средство. Напишите, что вас интересует.
                    </span>

                    {/* Крестик для закрытия */}
                    <button
                        onClick={(e) => {
                            e.stopPropagation();          // не открываем чат
                            setShowGreeting(false);
                            try { sessionStorage.setItem('ps_chatbot_greeting_dismissed', '1'); } catch (_) { }
                        }}
                        aria-label="Закрыть приветствие"
                        style={{
                            position: 'absolute',
                            top: '8px',
                            right: '10px',
                            background: 'none',
                            border: 'none',
                            color: '#888',
                            fontSize: '18px',
                            lineHeight: 1,
                            cursor: 'pointer',
                            padding: '2px 4px',
                            borderRadius: '6px',
                            transition: 'color 0.15s, background 0.15s',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.color = '#1C1917';
                            e.currentTarget.style.background = '#F5F0EB';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.color = '#888';
                            e.currentTarget.style.background = 'none';
                        }}
                    >
                        ✕
                    </button>

                    <div
                        style={{
                            position: 'absolute',
                            bottom: '-8px',
                            right: '32px',
                            width: '16px',
                            height: '16px',
                            backgroundColor: 'white',
                            borderRight: '1px solid #E8E3DD',
                            borderBottom: '1px solid #E8E3DD',
                            transform: 'rotate(45deg)',
                        }}
                    />
                </div>
            )}

            {/* Круглая иконка бота — видна всегда */}
            <button
                onClick={handleOpen}
                aria-label="Открыть чат с консультантом"
                style={{
                    position: 'fixed',
                    bottom: isMobile ? '16px' : '24px',
                    right: isMobile ? '16px' : '24px',
                    backgroundColor: '#6B7A6A',
                    color: 'white',
                    border: 'none',
                    borderRadius: '50%',
                    width: isMobile ? '52px' : '60px',
                    height: isMobile ? '52px' : '60px',
                    fontSize: isMobile ? '20px' : '24px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                    cursor: 'pointer',
                    zIndex: 999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'transform 0.2s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.05)')}
                onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
                🤖
            </button>

            {isOpen && (
                <div
                    style={{
                        position: 'fixed',
                        bottom: isMobile ? '76px' : '100px',
                        right: isMobile ? '12px' : '24px',
                        left: isMobile ? '12px' : 'auto',
                        width: isMobile ? 'auto' : '380px',
                        maxWidth: isMobile ? 'calc(100vw - 24px)' : '90vw',
                        backgroundColor: 'white',
                        borderRadius: '20px',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                        border: '1px solid #E8E3DD',
                        zIndex: 1000,
                        display: 'flex',
                        flexDirection: 'column',
                        maxHeight: isMobile ? 'calc(100vh - 100px)' : '550px',
                        boxSizing: 'border-box',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '12px 16px',
                            backgroundColor: '#FBF9F6',
                            borderBottom: '1px solid #E8E3DD',
                            borderRadius: '20px 20px 0 0',
                            flexShrink: 0,
                        }}
                    >
                        <span style={{ fontWeight: 600, color: '#1C1917' }}>🤖 ИИ-консультант</span>
                        <button
                            onClick={() => setIsOpen(false)}
                            aria-label="Закрыть чат"
                            style={{
                                background: 'none',
                                border: 'none',
                                fontSize: '18px',
                                cursor: 'pointer',
                                color: '#888',
                                padding: 0,
                                lineHeight: 1,
                            }}
                        >
                            ✕
                        </button>
                    </div>
                    <div
                        onClick={handleMessagesClick}
                        style={{
                            flex: 1,
                            overflowY: 'auto',
                            padding: '12px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            backgroundColor: 'white',
                            maxHeight: isMobile ? 'calc(100vh - 260px)' : '380px',
                        }}
                    >
                        {messages.map((msg, idx) => {
                            const bubbleBase = {
                                padding: '10px 14px',
                                fontSize: '0.9rem',
                                lineHeight: 1.45,
                                wordBreak: 'break-word',
                                overflowWrap: 'anywhere',
                            };
                            if (msg.from === 'bot') {
                                const style = {
                                    ...bubbleBase,
                                    alignSelf: 'flex-start',
                                    backgroundColor: '#F0ECE8',
                                    color: '#1C1917',
                                    borderRadius: '18px 18px 18px 4px',
                                    maxWidth: msg.wide ? '100%' : '88%',
                                };
                                return msg.html ? (
                                    <div key={idx} style={style} dangerouslySetInnerHTML={{ __html: msg.html }} />
                                ) : (
                                    <div key={idx} style={style}>{msg.text}</div>
                                );
                            }
                            return (
                                <div
                                    key={idx}
                                    style={{
                                        ...bubbleBase,
                                        alignSelf: 'flex-end',
                                        backgroundColor: '#6B7A6A',
                                        color: 'white',
                                        borderRadius: '18px 18px 4px 18px',
                                        maxWidth: '85%',
                                    }}
                                >
                                    {msg.text}
                                </div>
                            );
                        })}
                        {loading && (
                            <div style={{ color: '#888', fontSize: '0.9rem', padding: '4px 8px' }}>
                                Бот печатает...
                            </div>
                        )}
                        <div ref={bottomRef} />
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            gap: '8px',
                            padding: '12px',
                            borderTop: '1px solid #E8E3DD',
                            backgroundColor: 'white',
                            borderRadius: '0 0 20px 20px',
                            flexShrink: 0,
                        }}
                    >
                        <input
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                            placeholder="Спросите меня..."
                            style={{
                                flex: 1,
                                minWidth: 0,
                                padding: '8px 16px',
                                borderRadius: '100px',
                                border: '1px solid #E8E3DD',
                                fontSize: '0.9rem',
                                outline: 'none',
                                boxSizing: 'border-box',
                            }}
                        />
                        <button
                            onClick={handleSend}
                            disabled={loading}
                            aria-label="Отправить сообщение"
                            style={{
                                backgroundColor: '#6B7A6A',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '40px',
                                height: '40px',
                                flexShrink: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                fontSize: '18px',
                                opacity: loading ? 0.6 : 1,
                            }}
                        >
                            ➤
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default ChatBot;